import { IconButton as DesignIconButton, Tooltip as DesignTooltip } from "@mui/material";
import { CloseOutlined as DesignClose, } from "@mui/icons-material";
// AdminSettingsDrawer
//
// Right-anchored drawer surfacing two admin-only tabs:
//   1. Unsubscribes — read-only paginated suppression list (Agent A's opt-outs
//      only, scoped per-user-id by the /admin/suppressions endpoint)
//   2. Role Settings — admin/agent dropdown × 6 Send-List feature toggles. Each
//      feature has two values: `_allowed_toggle` (hides the toggle in the
//      Send-List UI when false) + `_default` (locked or initial value).
//
// Backend contract: GET/PUT /admin/role-settings + GET /admin/suppressions
// (PR #72 backend scaffold + migration 021 role_settings table).
//
// PR 3 of the Admin Settings rollout will wire SendListingsDialog to read
// role_settings on mount and apply the values; this drawer is the authoring
// surface for those values.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import {
  AdminSettingsRole,
  AdminSettingsSendMode,
  AdminUserRow,
  AssignableRole,
  EmailPreferenceRow,
  ROLE_SETTINGS_FIELDS,
  RoleSettingsPatch,
  RoleSettingsRow,
  SuppressionRow,
  createAdminUser,
  listAdminUsers,
  listEmailPreferences,
  listRoleSettings,
  listSuppressions,
  updateRoleSettings,
  updateUserRole,
} from "../../services/adminSettingsService";

interface AdminSettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

/** Coerce a row value for the field spec into the union type the UI works
 *  with. Server enforces shape so this is only a typescript bridge. */
function readDefault(
  row: RoleSettingsRow,
  field: keyof RoleSettingsRow
): boolean | AdminSettingsSendMode {
  return row[field] as boolean | AdminSettingsSendMode;
}

/** Compute the diff between original + draft so PUT only sends fields the
 *  admin actually changed. Backend rejects empty patches with 400 so the
 *  Save button stays disabled when nothing is dirty. */
function diffRow(
  original: RoleSettingsRow,
  draft: RoleSettingsRow
): RoleSettingsPatch {
  const patch: RoleSettingsPatch = {};
  for (const field of ROLE_SETTINGS_FIELDS) {
    if (original[field.allowedField] !== draft[field.allowedField]) {
      // @ts-expect-error — narrowed by field spec at runtime
      patch[field.allowedField] = draft[field.allowedField];
    }
    if (original[field.defaultField] !== draft[field.defaultField]) {
      // @ts-expect-error — narrowed by field spec at runtime
      patch[field.defaultField] = draft[field.defaultField];
    }
  }
  return patch;
}

const AdminSettingsDrawer: React.FC<AdminSettingsDrawerProps> = ({
  open,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<"role" | "users" | "optouts">("role");

  // ── Role Settings state ──────────────────────────────────────────
  const [roleRows, setRoleRows] = useState<RoleSettingsRow[]>([]);
  const [draftRows, setDraftRows] = useState<RoleSettingsRow[]>([]);
  const [selectedRole, setSelectedRole] = useState<AdminSettingsRole>("agent");
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [roleSaved, setRoleSaved] = useState<string | null>(null);

  // ── Opt-outs state (combines suppressions + per-mode preferences) ─
  const [suppressions, setSuppressions] = useState<SuppressionRow[]>([]);
  const [preferences, setPreferences] = useState<EmailPreferenceRow[]>([]);
  const [loadingOptOuts, setLoadingOptOuts] = useState(false);
  const [optOutsError, setOptOutsError] = useState<string | null>(null);

  // ── Users state ──────────────────────────────────────────────────
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [usersError, setUsersError] = useState<string | null>(null);
  const [savingUserId, setSavingUserId] = useState<string | null>(null);
  const [userSavedNote, setUserSavedNote] = useState<string | null>(null);
  // ── Create-user modal state ─────────────────────────────────────
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createUserEmail, setCreateUserEmail] = useState("");
  const [createUserPassword, setCreateUserPassword] = useState("");
  const [createUserFullName, setCreateUserFullName] = useState("");
  const [createUserRole, setCreateUserRole] = useState<AssignableRole>("agent");
  const [creatingUser, setCreatingUser] = useState(false);
  const [createUserError, setCreateUserError] = useState<string | null>(null);

  const loadRoles = useCallback(async () => {
    setLoadingRoles(true);
    setRoleError(null);
    try {
      const rows = await listRoleSettings();
      setRoleRows(rows);
      setDraftRows(rows.map((r) => ({ ...r })));
    } catch (err) {
      setRoleError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoadingRoles(false);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    setLoadingUsers(true);
    setUsersError(null);
    try {
      const rows = await listAdminUsers();
      setUsers(rows);
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : "Failed to load users");
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const handleRoleChange = useCallback(
    async (userRow: AdminUserRow, newRole: AssignableRole) => {
      // Skip no-op
      if (userRow.role === newRole) return;

      // Demoting an admin requires explicit confirmation
      if (userRow.role === "admin" && newRole !== "admin") {
        const ok = window.confirm(
          `Demote ${userRow.email || userRow.id} from admin to ${newRole}?\n\n` +
            "They will lose access to the Admin Dashboard + Admin Settings drawer + " +
            "system configuration. This is reversible — you can promote them back later."
        );
        if (!ok) return;
      }

      setSavingUserId(userRow.id);
      setUsersError(null);
      setUserSavedNote(null);
      try {
        const result = await updateUserRole(userRow.id, newRole);
        if (result.changed) {
          setUsers((prev) =>
            prev.map((u) => (u.id === userRow.id ? result.user : u))
          );
          setUserSavedNote(
            `Updated ${userRow.email || userRow.id} to ${newRole}.`
          );
        }
      } catch (err) {
        setUsersError(err instanceof Error ? err.message : "Update failed");
      } finally {
        setSavingUserId(null);
      }
    },
    []
  );

  const resetCreateUserForm = useCallback(() => {
    setCreateUserEmail("");
    setCreateUserPassword("");
    setCreateUserFullName("");
    setCreateUserRole("agent");
    setCreateUserError(null);
  }, []);

  const handleCreateUser = useCallback(async () => {
    const email = createUserEmail.trim().toLowerCase();
    const password = createUserPassword;
    if (!email || !email.includes("@")) {
      setCreateUserError("Enter a valid email.");
      return;
    }
    if (password.length < 8) {
      setCreateUserError("Password must be at least 8 characters.");
      return;
    }
    setCreatingUser(true);
    setCreateUserError(null);
    try {
      await createAdminUser({
        email,
        password,
        role: createUserRole,
        full_name: createUserFullName.trim() || undefined,
      });
      // Close modal + reset + refresh user list
      setCreateUserOpen(false);
      resetCreateUserForm();
      await loadUsers();
      setUserSavedNote(`Created ${email} as ${createUserRole}.`);
    } catch (err) {
      setCreateUserError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setCreatingUser(false);
    }
  }, [
    createUserEmail,
    createUserPassword,
    createUserRole,
    createUserFullName,
    loadUsers,
    resetCreateUserForm,
  ]);

  const loadOptOutsList = useCallback(async () => {
    setLoadingOptOuts(true);
    setOptOutsError(null);
    try {
      // Fetch both surfaces in parallel — admin needs to see every opt-out
      // state for a recipient, not just full unsubscribes.
      const [supResult, prefResult] = await Promise.all([
        listSuppressions(100, 0),
        listEmailPreferences(100, 0),
      ]);
      setSuppressions(supResult.suppressions);
      setPreferences(prefResult.preferences);
    } catch (err) {
      setOptOutsError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoadingOptOuts(false);
    }
  }, []);

  // Load all surfaces when drawer opens; only refetch when re-opening.
  useEffect(() => {
    if (!open) return;
    void loadRoles();
    void loadOptOutsList();
    void loadUsers();
  }, [open, loadRoles, loadOptOutsList, loadUsers]);

  const originalRow = useMemo(
    () => roleRows.find((r) => r.role === selectedRole),
    [roleRows, selectedRole]
  );
  const draftRow = useMemo(
    () => draftRows.find((r) => r.role === selectedRole),
    [draftRows, selectedRole]
  );

  const dirtyPatch = useMemo(() => {
    if (!originalRow || !draftRow) return null;
    const patch = diffRow(originalRow, draftRow);
    return Object.keys(patch).length > 0 ? patch : null;
  }, [originalRow, draftRow]);

  const updateDraftField = useCallback(
    (
      field: keyof RoleSettingsRow,
      value: boolean | AdminSettingsSendMode
    ) => {
      setDraftRows((prev) =>
        prev.map((r) =>
          r.role === selectedRole ? { ...r, [field]: value } : r
        )
      );
      setRoleSaved(null);
    },
    [selectedRole]
  );

  const handleSave = useCallback(async () => {
    if (!dirtyPatch || !draftRow) return;
    setSavingRole(true);
    setRoleError(null);
    setRoleSaved(null);
    try {
      const updated = await updateRoleSettings(selectedRole, dirtyPatch);
      setRoleRows((prev) =>
        prev.map((r) => (r.role === selectedRole ? updated : r))
      );
      setDraftRows((prev) =>
        prev.map((r) => (r.role === selectedRole ? updated : r))
      );
      setRoleSaved(`Saved ${selectedRole} settings.`);
    } catch (err) {
      setRoleError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setSavingRole(false);
    }
  }, [dirtyPatch, draftRow, selectedRole]);

  const handleRevert = useCallback(() => {
    if (!originalRow) return;
    setDraftRows((prev) =>
      prev.map((r) => (r.role === selectedRole ? { ...originalRow } : r))
    );
    setRoleSaved(null);
    setRoleError(null);
  }, [originalRow, selectedRole]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          maxWidth: "100%",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Admin Settings
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_e, v) => setActiveTab(v)}
        variant="fullWidth"
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label="Role Settings" value="role" />
        <Tab label="Users" value="users" />
        <Tab label="Opt-outs" value="optouts" />
      </Tabs>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", px: 3, py: 2 }}>
        {activeTab === "role" && (
          <Stack spacing={2}>
            {/* Compact header — role dropdown + info icon, no paragraph copy */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <FormControl size="small" sx={{ minWidth: 160 }}>
                <Select
                  value={selectedRole}
                  onChange={(e) =>
                    setSelectedRole(e.target.value as AdminSettingsRole)
                  }
                  disabled={loadingRoles}
                >
                  <MenuItem value="agent">Agent</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                </Select>
              </FormControl>
              <Tooltip
                title="Show toggle = whether this role can flip the option in Send List. Default = the locked/initial value when the toggle is hidden or first opens."
                placement="bottom-start"
              >
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{ color: "text.secondary", cursor: "help" }}
                />
              </Tooltip>
            </Box>

            {loadingRoles && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {roleError && <Alert severity="error">{roleError}</Alert>}
            {roleSaved && <Alert severity="success">{roleSaved}</Alert>}

            {!loadingRoles && !roleError && !draftRow && <Stack spacing={1} sx={{py: 2}}>
              <Chip size="small" variant="outlined" label="Not set up yet" sx={{alignSelf: "flex-start", color: "text.secondary"}} />
              <Typography variant="body2" color="text.secondary">No settings were returned for this role.</Typography>
            </Stack>}
            {!loadingRoles && draftRow && (
              <Box>
                {/* Column headers */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "1fr 70px 110px",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1,
                    py: 0.5,
                    borderBottom: 1,
                    borderColor: "divider",
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{ textTransform: "uppercase", letterSpacing: "0.05em" }}
                  >
                    Setting
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      textAlign: "center",
                    }}
                  >
                    Show
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    fontWeight={600}
                    sx={{
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      textAlign: "center",
                    }}
                  >
                    Default
                  </Typography>
                </Box>

                {/* One row per setting */}
                {ROLE_SETTINGS_FIELDS.map((field) => {
                  const allowed = draftRow[field.allowedField] as boolean;
                  const defaultValue = readDefault(draftRow, field.defaultField);

                  return (
                    <Box
                      key={field.key}
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 70px 110px",
                        alignItems: "center",
                        gap: 1.5,
                        px: 1,
                        py: 0.75,
                        borderBottom: 1,
                        borderColor: "divider",
                        opacity: allowed ? 1 : 0.85,
                      }}
                    >
                      {/* Setting name + info icon */}
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {field.label}
                        </Typography>
                        {field.description && (
                          <Tooltip title={field.description} placement="top">
                            <InfoOutlinedIcon
                              fontSize="inherit"
                              sx={{
                                color: "text.disabled",
                                cursor: "help",
                                fontSize: 14,
                                flexShrink: 0,
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>

                      {/* Show-toggle column */}
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Switch
                          size="small"
                          checked={allowed}
                          onChange={(e) =>
                            updateDraftField(
                              field.allowedField,
                              e.target.checked
                            )
                          }
                          disabled={savingRole}
                        />
                      </Box>

                      {/* Default column */}
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        {field.defaultControl === "boolean" ? (
                          <Switch
                            size="small"
                            checked={defaultValue as boolean}
                            onChange={(e) =>
                              updateDraftField(
                                field.defaultField,
                                e.target.checked
                              )
                            }
                            disabled={savingRole}
                          />
                        ) : (
                          <FormControl size="small" sx={{ width: "100%" }}>
                            <Select
                              value={defaultValue as AdminSettingsSendMode}
                              onChange={(e) =>
                                updateDraftField(
                                  field.defaultField,
                                  e.target.value as AdminSettingsSendMode
                                )
                              }
                              disabled={savingRole}
                              sx={{ fontSize: 12 }}
                            >
                              <MenuItem value="personal">Personal</MenuItem>
                              <MenuItem value="ultra_marketing">Ultra Marketing</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    </Box>
                  );
                })}

                {originalRow?.updated_at && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 1.5, px: 1 }}
                  >
                    Last saved: {new Date(originalRow.updated_at).toLocaleString()}
                  </Typography>
                )}
              </Box>
            )}
          </Stack>
        )}

        {activeTab === "users" && (
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Promote · demote · create. Demo accounts are read-only.
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip title="Create new user">
                  <IconButton
                    size="small"
                    onClick={() => {
                      resetCreateUserForm();
                      setCreateUserOpen(true);
                    }}
                    aria-label="Add user"
                  >
                    <PersonAddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  size="small"
                  onClick={loadUsers}
                  disabled={loadingUsers}
                  aria-label="Refresh users"
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {loadingUsers && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {usersError && <Alert severity="error">{usersError}</Alert>}
            {userSavedNote && <Alert severity="success">{userSavedNote}</Alert>}

            {!loadingUsers && users.length > 0 && (
              <Stack spacing={1}>
                {users.map((u) => {
                  const isDemoUser = u.role === "demo";
                  const saving = savingUserId === u.id;
                  return (
                    <Paper
                      key={u.id}
                      variant="outlined"
                      sx={{ px: 2, py: 1.25 }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {u.email || u.full_name || u.id.slice(0, 8) + "…"}
                          </Typography>
                          {u.full_name && u.email && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {u.full_name}
                            </Typography>
                          )}
                        </Box>
                        {isDemoUser ? (
                          <Chip
                            label="demo (locked)"
                            size="small"
                            variant="outlined"
                          />
                        ) : (
                          <FormControl size="small" sx={{ minWidth: 130 }}>
                            <Select
                              value={u.role}
                              disabled={saving}
                              onChange={(e) =>
                                handleRoleChange(
                                  u,
                                  e.target.value as AssignableRole
                                )
                              }
                            >
                              <MenuItem value="admin">Admin</MenuItem>
                              <MenuItem value="manager">Manager</MenuItem>
                              <MenuItem value="agent">Agent</MenuItem>
                            </Select>
                          </FormControl>
                        )}
                      </Box>
                    </Paper>
                  );
                })}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ pt: 1 }}
                >
                  Showing {users.length} user{users.length === 1 ? "" : "s"}
                </Typography>
              </Stack>
            )}

            {!loadingUsers && !usersError && users.length === 0 && (
              <Paper variant="outlined" sx={{ px: 2, py: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No users found.
                </Typography>
              </Paper>
            )}
          </Stack>
        )}

        {activeTab === "optouts" && (
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Recipients who opted out of your sends (full unsubscribes +
                per-mode opt-outs from the Personal-mode preferences center).
              </Typography>
              <IconButton
                size="small"
                onClick={loadOptOutsList}
                disabled={loadingOptOuts}
                aria-label="Refresh opt-outs"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Box>

            {loadingOptOuts && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {optOutsError && <Alert severity="error">{optOutsError}</Alert>}

            {!loadingOptOuts &&
              !optOutsError &&
              suppressions.length === 0 &&
              preferences.length === 0 && (
                <Paper variant="outlined" sx={{ px: 2, py: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No opt-outs for your account.
                  </Typography>
                </Paper>
              )}

            {!loadingOptOuts && suppressions.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    display: "block",
                    mb: 0.75,
                  }}
                >
                  Full unsubscribes ({suppressions.length})
                </Typography>
                <Stack spacing={1}>
                  {suppressions.map((row) => (
                    <Paper key={row.id} variant="outlined" sx={{ px: 2, py: 1.25 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          gap: 2,
                        }}
                      >
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" fontWeight={500} noWrap>
                            {row.recipient_email}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block" }}
                          >
                            {new Date(row.unsubscribed_at).toLocaleString()}
                          </Typography>
                        </Box>
                        <Chip
                          label={row.reason || "unsubscribed"}
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ flexShrink: 0 }}
                        />
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </Box>
            )}

            {!loadingOptOuts && preferences.length > 0 && (
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                  sx={{
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                    display: "block",
                    mb: 0.75,
                  }}
                >
                  Per-mode opt-outs ({preferences.length})
                </Typography>
                <Stack spacing={1}>
                  {preferences.map((row) => {
                    const directOff = !row.direct_email_enabled;
                    const marketingOff = !row.marketing_email_enabled;
                    return (
                      <Paper key={row.id} variant="outlined" sx={{ px: 2, py: 1.25 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            gap: 1,
                          }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={500} noWrap>
                              {row.recipient_email}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block" }}
                            >
                              {new Date(row.updated_at).toLocaleString()}
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              gap: 0.5,
                              flexShrink: 0,
                              flexWrap: "wrap",
                              justifyContent: "flex-end",
                            }}
                          >
                            {directOff && (
                              <Chip
                                label="Direct off"
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                            {marketingOff && (
                              <Chip
                                label="Marketing off"
                                size="small"
                                color="warning"
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {/* Footer — only render Save/Revert on Role tab */}
      {activeTab === "role" && (
        <Box
          sx={{
            borderTop: 1,
            borderColor: "divider",
            px: 3,
            py: 2,
            display: "flex",
            gap: 1.5,
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="text"
            onClick={handleRevert}
            disabled={!dirtyPatch || savingRole}
            size="small"
          >
            Revert
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!dirtyPatch || savingRole}
            size="small"
          >
            {savingRole ? "Saving…" : "Save"}
          </Button>
        </Box>
      )}

      {/* Create-user modal — opened from Users tab */}
      <Dialog
        open={createUserOpen}
        onClose={() => !creatingUser && setCreateUserOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ pr: 7, position: "relative" }}>Create new user<span style={{ position: "absolute", right: 8, top: 8 }}><DesignTooltip title="Close"><span><DesignIconButton aria-label="Close" onClick={() => setCreateUserOpen(false)} disabled={creatingUser}  sx={{ width: 40, height: 40 }}><DesignClose sx={{ fontSize: 20 }} /></DesignIconButton></span></DesignTooltip></span></DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={createUserEmail}
              onChange={(e) => setCreateUserEmail(e.target.value)}
              size="small"
              fullWidth
              disabled={creatingUser}
              autoFocus
            />
            <TextField
              label="Initial password"
              type="password"
              value={createUserPassword}
              onChange={(e) => setCreateUserPassword(e.target.value)}
              size="small"
              fullWidth
              disabled={creatingUser}
              helperText="Min 8 chars. User can change later in Settings."
            />
            <TextField
              label="Full name (optional)"
              value={createUserFullName}
              onChange={(e) => setCreateUserFullName(e.target.value)}
              size="small"
              fullWidth
              disabled={creatingUser}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                label="Role"
                value={createUserRole}
                onChange={(e) =>
                  setCreateUserRole(e.target.value as AssignableRole)
                }
                disabled={creatingUser}
              >
                <MenuItem value="agent">Agent</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            {createUserError && (
              <Alert severity="error" variant="outlined">
                {createUserError}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreateUserOpen(false)}
            disabled={creatingUser}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateUser}
            disabled={creatingUser}
          >
            {creatingUser ? "Creating…" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default AdminSettingsDrawer;
